import React from "react";
import { useTranslation } from "react-i18next";
import { BellRing, CalendarClock, ChevronDown, Edit2, FileText, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useCurrency } from "@/contexts/CurrencyContext";
import { paidAndLeft } from "@/lib/payments/paymentsView";
import { statusBadgeVariant } from "./paymentBadges";
import type { TransactionData } from "@/types/types";
import type { CoverageInfo } from "@/types/payments";

interface MobilePaymentCardProps {
  tx: TransactionData;
  cov?: CoverageInfo;
  expanded: boolean;
  loadingPayments: boolean;
  payments: TransactionData[];
  canExpand: boolean;
  onToggleExpand: () => void;
  onViewInvoice: () => void;
  onSplitPayment?: () => void;
  /** Relance d'impayé — le bouton n'apparaît que si un restant est dû. */
  onRemind?: () => void;
  onEditPayment?: (p: TransactionData) => void;
}

/**
 * Ligne de paiement en carte pour mobile (< md) — pendant de la ligne de
 * table desktop de TransactionList. Cibles tactiles ≥ 36 px (h-9),
 * typographies jamais sous text-xs.
 */
export const MobilePaymentCard: React.FC<MobilePaymentCardProps> = ({
  tx,
  cov,
  expanded,
  loadingPayments,
  payments,
  canExpand,
  onToggleExpand,
  onViewInvoice,
  onSplitPayment,
  onRemind,
  onEditPayment,
}) => {
  const { t } = useTranslation(["payments", "common"]);
  const { formatCurrency } = useCurrency();

  const { paid: amountPaid, left: amountLeft } = paidAndLeft(tx, cov);
  const pendingAmount = Number(cov?.pendingAmount ?? 0);
  const paymentsCount = cov?.paymentsCount ?? 0;

  return (
    <div className="rounded-2xl border bg-card p-3 flex flex-col gap-3">
      {/* Identité + statut */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium truncate">{tx.client || "—"}</div>
          <div className="mt-0.5 text-xs text-muted-foreground truncate">
            {tx.date || "—"} • {t(`methods.${tx.paymentMethod}`, { defaultValue: tx.paymentMethod || "—" })}
            {tx.description ? ` • ${tx.description}` : ""}
          </div>
        </div>
        <Badge className="shrink-0" variant={statusBadgeVariant(tx.status) as any}>
          {t(`statuses.${(tx.status || "").toLowerCase()}`, { defaultValue: tx.status || "—" })}
        </Badge>
      </div>

      {/* Montants */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: t("amount"), value: typeof tx.amount === "number" ? formatCurrency(tx.amount) : tx.amount || "—" },
          { label: t("amountPaid", { defaultValue: "Payé" }), value: formatCurrency(amountPaid) },
          { label: t("amountLeft", { defaultValue: "Restant" }), value: formatCurrency(amountLeft) },
        ].map((s) => (
          <div key={s.label} className="rounded-lg bg-muted/40 px-2 py-1.5">
            <div className="text-[11px] text-muted-foreground">{s.label}</div>
            <div className="text-sm font-semibold tabular-nums truncate">{s.value}</div>
          </div>
        ))}
      </div>
      {pendingAmount > 0 && (
        <div className="text-xs text-muted-foreground -mt-1.5">
          {t("kpi.pending", { defaultValue: "En attente" })} : {formatCurrency(pendingAmount)}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between gap-2">
        {canExpand ? (
          <Button variant="ghost" className="h-9 px-2 text-sm" onClick={onToggleExpand}>
            <ChevronDown
              className={`h-4 w-4 mr-1.5 transition-transform ${expanded ? "rotate-180" : ""}`}
            />
            {t("payments.showCount", { count: paymentsCount, defaultValue: "Voir les paiements ({{count}})" })}
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground px-2">
            {t("payments.count", { defaultValue: "Paiements" })} : {paymentsCount}
          </span>
        )}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            onClick={onViewInvoice}
            aria-label={t("invoice.preview")}
          >
            <FileText className="h-4 w-4" />
          </Button>
          {onSplitPayment && (
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={onSplitPayment}
              aria-label={t("splitPayment.title")}
            >
              <CalendarClock className="h-4 w-4" />
            </Button>
          )}
          {onRemind && amountLeft > 0 && (
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={onRemind}
              aria-label={t("remind.action", { defaultValue: "Relancer" })}
            >
              <BellRing className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Détail des paiements (accordéon) */}
      {expanded && (
        <div className="border-t pt-2 space-y-2">
          {loadingPayments ? (
            <div className="flex items-center gap-2 py-1 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {t("common:loading", { defaultValue: "Chargement..." })}
            </div>
          ) : payments.length > 0 ? (
            payments.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-2 rounded-lg border p-2">
                <div className="min-w-0 leading-tight">
                  <div className="text-sm font-medium truncate">{p.description || "Payment"}</div>
                  <div className="text-xs text-muted-foreground">
                    {p.date} • {t(`methods.${p.paymentMethod}`, { defaultValue: p.paymentMethod })}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant={statusBadgeVariant(p.status) as any} className="text-xs">
                    {t(`statuses.${(p.status || "").toLowerCase()}`, { defaultValue: p.status || "—" })}
                  </Badge>
                  <span className="text-sm font-semibold tabular-nums whitespace-nowrap">
                    {typeof p.amount === "number" ? formatCurrency(p.amount) : p.amount}
                  </span>
                  {onEditPayment && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9"
                      onClick={() => onEditPayment(p)}
                      aria-label={t("common:edit", { defaultValue: "Modifier" })}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="py-1 text-xs text-muted-foreground">
              {t("common:noData", { defaultValue: "Aucune donnée" })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
